import { Vec2 } from './math';

export type LinetypeId =
  | 'continuous'
  | 'dashed'
  | 'dotted'
  | 'hidden'
  | 'center'
  | 'dashdot'
  | 'divide';

/**
 * Dash/gap lengths in millimetres (world units) — the seven patterns the node editor offers,
 * plus the layer presets that use them. A zero-length entry is drawn as a dot.
 */
export const LINETYPE_PATTERNS: Record<LinetypeId, number[]> = {
  continuous: [],
  dashed: [6, 3],
  dotted: [0.35, 2],
  hidden: [3, 1.5],
  center: [12, 3, 3, 3],
  dashdot: [12, 3, 0.5, 3],
  divide: [12, 3, 0.5, 3, 0.5, 3],
};

/** Display order and labels for the linetype pickers. */
export const LINETYPES: ReadonlyArray<{ id: LinetypeId; label: string }> = [
  { id: 'continuous', label: 'Continuous' },
  { id: 'dashed', label: 'Dashed' },
  { id: 'dotted', label: 'Dotted' },
  { id: 'hidden', label: 'Hidden' },
  { id: 'center', label: 'Centre' },
  { id: 'dashdot', label: 'Dash-dot' },
  { id: 'divide', label: 'Divide' },
];

export interface Layer {
  id: string;
  name: string;
  /** 0xRRGGBB */
  color: number;
  /** Screen pixels at 1:1 zoom (CAD lineweight preview). */
  lineWidth: number;
  linetype: LinetypeId;
  visible: boolean;
  locked: boolean;
}

export const DEFAULT_LAYER_ID = '0';

export interface LayerInit {
  color?: number;
  lineWidth?: number;
  linetype?: LinetypeId;
  visible?: boolean;
  locked?: boolean;
}

export function makeLayer(name: string, init: LayerInit = {}): Layer {
  return {
    id: name,
    name,
    color: init.color ?? 0xd7dde5,
    lineWidth: init.lineWidth ?? 1,
    linetype: init.linetype ?? 'continuous',
    visible: init.visible ?? true,
    locked: init.locked ?? false,
  };
}

/** Light drafting sheet: dark ink on a 95%-lightness background, green/orange accents. */
export function defaultLayers(): Layer[] {
  return [
    makeLayer(DEFAULT_LAYER_ID, { color: 0x1f2937 }),
    makeLayer('OUTLINE', { color: 0x111827, lineWidth: 1.4 }),
    makeLayer('CENTER', { color: 0x15803d, lineWidth: 1, linetype: 'center' }),
    makeLayer('HIDDEN', { color: 0x6b7280, lineWidth: 1, linetype: 'hidden' }),
    makeLayer('HATCH', { color: 0x4b5563, lineWidth: 0.8 }),
    makeLayer('TEXT', { color: 0xb45309, lineWidth: 1 }),
    makeLayer('DIM', { color: 0x15803d, lineWidth: 1 }),
  ];
}

/**
 * Splits a polyline into dash runs. Returns one flat path per continuous dash, so the
 * renderer can stroke everything with a single (fast) batched path.
 */
export function dashPolyline(
  points: readonly Vec2[],
  closed: boolean,
  pattern: readonly number[],
  phase = 0,
): Vec2[][] {
  if (pattern.length === 0 || points.length < 2) {
    return [closed ? [...points, points[0]!] : [...points]];
  }

  const runs: Vec2[][] = [];
  let run: Vec2[] = [];
  let patternIndex = 0;
  let remaining = pattern[0]!;
  let drawing = true;

  const total = pattern.reduce((a, b) => a + b, 0);
  let phaseLeft = total > 0 ? ((phase % total) + total) % total : 0;
  while (phaseLeft > 0) {
    if (phaseLeft >= remaining) {
      phaseLeft -= remaining;
      patternIndex = (patternIndex + 1) % pattern.length;
      remaining = pattern[patternIndex]!;
      drawing = patternIndex % 2 === 0;
    } else {
      remaining -= phaseLeft;
      phaseLeft = 0;
    }
  }

  const segments = [...points];
  if (closed) segments.push(points[0]!);

  for (let i = 0; i < segments.length - 1; i++) {
    let from = segments[i]!;
    const to = segments[i + 1]!;
    let segLength = from.distanceTo(to);
    if (segLength < 1e-12) continue;
    const dir = to.sub(from).div(segLength);

    while (segLength > 0) {
      const step = Math.min(segLength, remaining);
      const next = from.add(dir.scale(step));
      if (drawing) {
        if (run.length === 0) run.push(from);
        run.push(next);
      }
      from = next;
      segLength -= step;
      remaining -= step;
      if (remaining <= 1e-9) {
        if (drawing && run.length > 1) runs.push(run);
        run = [];
        patternIndex = (patternIndex + 1) % pattern.length;
        remaining = pattern[patternIndex]!;
        drawing = patternIndex % 2 === 0;
      }
    }
  }
  if (drawing && run.length > 1) runs.push(run);

  return runs;
}
